/**
 * Expiry — design/PROTOTYPE-SPEC.md §8.
 *
 * "Batches grouped by urgency (expired / <4 weeks / <12 weeks) or custom date
 * window. Expired-stock alert on login with one-tap write-off. Quarterly
 * write-off report with all-time total and per-batch drill-down."
 *
 * This is a BATCH view, not a product view. The dashboard counts products —
 * "how many lines need attention" — while here you are looking at physical
 * stock on a shelf, and two boxes of the same product with different expiry
 * dates are two different problems. See docs/DECISIONS.md §3.
 *
 * Write-off asks for a reason before it will do anything. The whole value of the
 * quarterly report is knowing whether a clinic is losing money to over-ordering
 * or to breakage, and a one-tap button with no reason destroys that.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { WRITE_OFF_REASONS, WRITE_OFF_REASON_LABEL } from '@inventory-tracker/core';
import {
  getExpiry,
  getWriteOffs,
  writeOffBatch,
  type ExpiryBatch,
  type ExpiryBucket,
  type ExpiryResponse,
  type WriteOffReason,
  type WriteOffReport,
} from '../lib/api.js';
import { c, field, label as labelStyle, mono, primaryButton, sans, segTab, segTrack } from '../theme.js';
import { Pill, cardStyle, formatDate, money, smallButton, unitCount } from './format.js';

type Tab = 'buckets' | 'report';

export function ExpiryPage({
  onChanged,
  refreshToken,
}: {
  onChanged: () => void;
  refreshToken: number;
}) {
  const [tab, setTab] = useState<Tab>('buckets');
  const [bucket, setBucket] = useState<ExpiryBucket>('all');
  const [windowDays, setWindowDays] = useState('');
  const [data, setData] = useState<ExpiryResponse | null>(null);
  const [report, setReport] = useState<WriteOffReport | null>(null);
  const [writingOff, setWritingOff] = useState<ExpiryBatch | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  /**
   * Sequence number of the most recent request. Responses that are not the
   * latest are discarded rather than rendered.
   *
   * Typing "40" into the custom window fires `window=4` then `window=40`, and
   * nothing guarantees they come back in that order. When the 4-day answer
   * landed second the page showed one batch under a box reading "40" — a wrong
   * answer, stated confidently, on the screen whose whole job is to stop expired
   * stock reaching a patient. Debouncing alone only makes that rarer.
   */
  const latestRequest = useRef(0);

  const load = useCallback(async () => {
    const seq = ++latestRequest.current;
    try {
      const params = new URLSearchParams();
      if (windowDays.trim()) params.set('window', windowDays.trim());
      else if (bucket !== 'all') params.set('bucket', bucket);
      const qs = params.toString();
      const res = await getExpiry(qs ? `?${qs}` : '');
      if (seq !== latestRequest.current) return;
      setData(res);
      setError(null);
    } catch (e) {
      if (seq !== latestRequest.current) return;
      setError(e instanceof Error ? e.message : 'Could not load expiry');
    } finally {
      if (seq === latestRequest.current) setLoading(false);
    }
  }, [bucket, windowDays, refreshToken]);

  useEffect(() => {
    // Debounced so typing a two-digit window does not fire a request per digit.
    const t = setTimeout(() => void load(), windowDays ? 250 : 0);
    return () => clearTimeout(t);
  }, [load, windowDays]);

  useEffect(() => {
    if (tab !== 'report' || report) return;
    void getWriteOffs()
      .then(setReport)
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : 'Could not load the write-off report'),
      );
  }, [tab, report]);

  async function afterWriteOff(message: string) {
    setFlash(message);
    setReport(null); // stale now
    await load();
    onChanged();
  }

  return (
    <div data-tour="expiry">
      {error && (
        <div
          role="alert"
          style={{
            background: c.dangerBg,
            border: `1px solid ${c.dangerBorder}`,
            borderRadius: 12,
            padding: '12px 16px',
            marginBottom: 16,
            fontSize: 13.5,
            color: c.danger,
          }}
        >
          {error}
        </div>
      )}
      {flash && (
        <div
          style={{
            background: c.okBg,
            border: `1px solid ${c.okLine}`,
            borderRadius: 12,
            padding: '12px 16px',
            marginBottom: 16,
            fontSize: 13.5,
            color: c.success,
          }}
        >
          {flash}
        </div>
      )}

      <div style={{ ...segTrack, marginBottom: 16 }}>
        {(
          [
            ['buckets', 'Expiring stock'],
            ['report', 'Write-off report'],
          ] as [Tab, string][]
        ).map(([key, lbl]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            style={segTab(tab === key)}
          >
            {lbl}
          </button>
        ))}
      </div>

      {tab === 'buckets' ? (
        <>
          {data && (
            <section
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 14,
                marginBottom: 18,
              }}
            >
              <Summary
                label="Expired"
                tone="expired"
                count={data.summary.expired.batches}
                valuePence={data.summary.expired.valuePence}
              />
              <Summary
                label={`Within ${data.thresholds.urgentWeeks} weeks`}
                tone="urgent"
                count={data.summary.urgent.batches}
                valuePence={data.summary.urgent.valuePence}
              />
              <Summary
                label={`Within ${data.thresholds.soonWeeks} weeks`}
                tone="soon"
                count={data.summary.soon.batches}
                valuePence={data.summary.soon.valuePence}
              />
            </section>
          )}

          <div
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              marginBottom: 14,
              flexWrap: 'wrap',
            }}
          >
            <div style={segTrack}>
            {(['all', 'expired', 'urgent', 'soon'] as ExpiryBucket[]).map((b) => (
              <button
                key={b}
                type="button"
                onClick={() => {
                  setBucket(b);
                  setWindowDays('');
                }}
                style={segTab(windowDays.trim() === '' && bucket === b)}
              >
                {b === 'all' ? 'All' : b === 'expired' ? 'Expired' : b === 'urgent' ? 'Urgent' : 'Soon'}
              </button>
            ))}
            </div>

            {/* Spec §8's "custom date window" — overrides the buckets entirely. */}
            <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={{ fontSize: 13, color: c.muted2 }} htmlFor="exp-window">
                Custom window
              </label>
              <input
                id="exp-window"
                value={windowDays}
                onChange={(e) => setWindowDays(e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="days"
                inputMode="numeric"
                style={{ ...field, width: 96, height: 38 }}
              />
            </span>
          </div>

          {loading ? (
            <p style={{ color: c.muted, fontSize: 14 }}>Loading…</p>
          ) : !data || data.batches.length === 0 ? (
            <div style={cardStyle}>
              <p style={{ margin: 0, fontSize: 13.5, color: c.muted }}>
                Nothing here. No batch matches that filter.
              </p>
            </div>
          ) : (
            <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
                  <thead>
                    <tr>
                      {['Product', 'Lot', 'Expires', 'Left', 'Value', ''].map((h, i) => (
                        <th
                          key={h || i}
                          style={{
                            textAlign: i === 4 ? 'right' : 'left',
                            padding: '11px 16px',
                            fontSize: 12.5,
                            textTransform: 'lowercase',
                            color: c.label,
                            fontWeight: 400,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.batches.map((b) => (
                      <tr key={b.batchId} style={{ borderTop: `1px solid ${c.border}` }}>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ fontWeight: 500 }}>{b.productName}</div>
                          {b.locationLabel && (
                            <div style={{ fontSize: 12, color: c.muted, marginTop: 2 }}>
                              {b.locationLabel}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px', color: c.muted2 }}>
                          {b.lot ?? '—'}
                        </td>
                        <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                          <Pill tone={b.expiryStatus}>
                            {b.expiryStatus === 'expired' ? 'Expired' : `${b.weeksLeft}w left`}
                          </Pill>
                          <div style={{ fontSize: 11.5, color: c.faint, marginTop: 3 }}>
                            {formatDate(b.expiry)}
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                          {unitCount(b.qtyRemaining, b.unitLabel)}
                        </td>
                        <td
                          style={{ padding: '12px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}
                        >
                          {money(b.valuePence)}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                          <button
                            type="button"
                            onClick={() => setWritingOff(b)}
                            style={smallButton}
                          >
                            Write off
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      ) : (
        <WriteOffReportView report={report} />
      )}

      {writingOff && (
        <WriteOffDialog
          batch={writingOff}
          onClose={() => setWritingOff(null)}
          onDone={async (msg) => {
            setWritingOff(null);
            await afterWriteOff(msg);
          }}
        />
      )}
    </div>
  );
}

function Summary({
  label,
  tone,
  count,
  valuePence,
}: {
  label: string;
  tone: 'expired' | 'urgent' | 'soon';
  count: number;
  valuePence: number;
}) {
  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Pill tone={tone}>{label}</Pill>
      </div>
      <div style={{ fontFamily: sans, letterSpacing: '-0.03em', fontSize: 32, fontWeight: 300, marginTop: 10, lineHeight: 1 }}>{count}</div>
      <div style={{ fontSize: 12.5, color: c.muted, marginTop: 6 }}>
        {count === 1 ? 'batch' : 'batches'} · {money(valuePence)}
      </div>
    </div>
  );
}

function WriteOffDialog({
  batch,
  onClose,
  onDone,
}: {
  batch: ExpiryBatch;
  onClose: () => void;
  onDone: (message: string) => void | Promise<void>;
}) {
  // Expired is the common case and the one the alert offers in one tap.
  const [reason, setReason] = useState<WriteOffReason>(
    batch.expiryStatus === 'expired' ? 'expired' : 'damaged',
  );
  const [note, setNote] = useState('');
  const [qty, setQty] = useState(String(batch.qtyRemaining));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const n = Number(qty);
    if (!Number.isInteger(n) || n < 1 || n > batch.qtyRemaining) {
      setError(`Enter between 1 and ${batch.qtyRemaining}`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await writeOffBatch(batch.batchId, {
        qty: n,
        reason,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      await onDone(
        `Wrote off ${res.writtenOff} × ${batch.productName} · ${money(res.valuePence)}`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not write that off');
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(20,26,22,0.42)',
        zIndex: 80,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(440px, 100%)',
          background: c.appBg,
          borderRadius: 18,
          padding: '22px 24px',
          fontFamily: sans,
          display: 'grid',
          gap: 14,
        }}
      >
        <div>
          <h2 style={{ fontFamily: sans, letterSpacing: '-0.03em', fontWeight: 300, fontSize: 22, margin: 0 }}>
            Write off stock
          </h2>
          <p style={{ fontSize: 13.5, color: c.muted, margin: '6px 0 0' }}>
            {batch.productName} · {batch.lot ?? 'no lot'} · expires {formatDate(batch.expiry)}
          </p>
        </div>

        {error && (
          <div style={{ fontSize: 13, color: c.danger }} role="alert">
            {error}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: 12 }}>
          <div>
            <label style={labelStyle} htmlFor="wo-qty">
              Quantity
            </label>
            <input
              id="wo-qty"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              inputMode="numeric"
              style={field}
            />
          </div>
          <div>
            <label style={labelStyle} htmlFor="wo-reason">
              Reason
            </label>
            <select
              id="wo-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value as WriteOffReason)}
              style={{ ...field, padding: '0 10px' }}
            >
              {WRITE_OFF_REASONS.map((r) => (
                <option key={r} value={r}>
                  {WRITE_OFF_REASON_LABEL[r]}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label style={labelStyle} htmlFor="wo-note">
            Note (optional)
          </label>
          <input
            id="wo-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            style={field}
          />
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button
            type="submit"
            disabled={busy}
            style={{ ...primaryButton, height: 46, opacity: busy ? 0.6 : 1 }}
          >
            {busy ? 'Writing off…' : 'Write off'}
          </button>
          <button type="button" onClick={onClose} style={{ ...smallButton, height: 46, padding: '0 18px' }}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

function WriteOffReportView({ report }: { report: WriteOffReport | null }) {
  if (!report) return <p style={{ color: c.muted, fontSize: 14 }}>Loading…</p>;

  if (report.entries.length === 0) {
    return (
      <div style={cardStyle}>
        <p style={{ margin: 0, fontSize: 13.5, color: c.muted }}>
          Nothing has been written off yet.
        </p>
      </div>
    );
  }

  return (
    <>
      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <div style={{ fontSize: 11, textTransform: 'lowercase', fontFamily: mono, color: c.label }}>
          Written off, all time
        </div>
        <div style={{ fontFamily: sans, letterSpacing: '-0.03em', fontSize: 38, fontWeight: 300, marginTop: 8, lineHeight: 1 }}>
          {money(report.total.valuePence)}
        </div>
        <div style={{ fontSize: 13, color: c.muted, marginTop: 7 }}>
          {report.total.qty} {report.total.qty === 1 ? 'unit' : 'units'} ·{' '}
          {Object.entries(report.byReason)
            .map(
              ([reason, v]) =>
                `${WRITE_OFF_REASON_LABEL[reason as WriteOffReason] ?? reason} ${money(
                  v.valuePence,
                )}`,
            )
            .join(' · ')}
        </div>
      </div>

      <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
            <thead>
              <tr>
                {['Date', 'Product', 'Lot', 'Qty', 'Reason', 'By', 'Value'].map((h, i) => (
                  <th
                    key={h}
                    style={{
                      textAlign: i === 6 ? 'right' : 'left',
                      padding: '11px 16px',
                      fontSize: 12.5,
                      textTransform: 'lowercase',
                      color: c.label,
                      fontWeight: 400,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {report.entries.map((e) => (
                <tr key={e.movementId} style={{ borderTop: `1px solid ${c.border}` }}>
                  <td style={{ padding: '11px 16px', whiteSpace: 'nowrap' }}>
                    {formatDate(e.occurredOn)}
                  </td>
                  <td style={{ padding: '11px 16px', fontWeight: 500 }}>{e.productName}</td>
                  <td style={{ padding: '11px 16px', color: c.muted2 }}>{e.lot ?? '—'}</td>
                  <td style={{ padding: '11px 16px' }}>{e.qty}</td>
                  <td style={{ padding: '11px 16px', color: c.muted2 }}>
                    {WRITE_OFF_REASON_LABEL[e.reason as WriteOffReason] ?? e.reason}
                    {e.note && (
                      <div style={{ fontSize: 12, color: c.faint, marginTop: 2 }}>{e.note}</div>
                    )}
                  </td>
                  <td style={{ padding: '11px 16px', color: c.muted2 }}>{e.byName ?? '—'}</td>
                  <td style={{ padding: '11px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {money(e.valuePence)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
